'use client';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { msg } from '@/services/isomorphic/I18NService';
import { FC, useRef, type ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';
import LocaleListVM from '@/react/view-models/LocaleListVM';
import { Button } from '../../core-ui/Button';
import { Icon, IconSizeProp, IconTypes } from '../../core-ui/Icon';

import S from './styles.base.module.scss';
import { country_search_title } from "@/lang/__generated__/ahnu/country_search_title";
import { country_search_placeholder } from "@/lang/__generated__/ahnu/country_search_placeholder";

/**
 * Properties for the SearchBox component.
 */
export interface ICountrySearchBoxProps {
  /** String of classes for className. */
  className?: string;

  /**
   * Whether it is the mobile navigation variant.
   */
  isMobileNav?: boolean;

  /**
   * The view model for the locale list.
   */
  vm: LocaleListVM;
}

/**
 * Customized Search Form with Input and Svg Icon embeded.
 */
const CountrySearchBox: FC<ICountrySearchBoxProps> = observer(
  function CountrySearchBox({ className, vm, isMobileNav = false }) {
    // Reference to the input element, used to manage focus and blur.
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
      vm.filter(e.target.value);
    };

    return (
      <div className={classes(S.searchInput, isMobileNav && S.mobileNav)}>
        <form
          role="search"
          className={classes(className, S.form, 'primary')}
          onSubmit={(e) => e.preventDefault()}
        >
          <div className={S.buttonsRow}>
            <Button
              title={msg(country_search_title)}
              variant="text"
              className={S.searchButton}
              ariaLabel={msg(country_search_placeholder)}
            >
              <Icon
                size={IconSizeProp.SizeHeavy}
                icon={IconTypes.Search}
                className={classes(S.formIcon, 'primary')}
              />
            </Button>
          </div>
          <input
            ref={inputRef}
            title={msg(country_search_title)}
            aria-label={msg(country_search_placeholder)}
            placeholder={msg(country_search_placeholder)}
            className={classes(S.searchInput, 'primary')}
            onChange={handleInputChange}
          />
        </form>
      </div>
    );
  }
);

export default CountrySearchBox;
